import "./CountertopUniversityForm.scss"
import {useParams} from "react-router-dom";
import {LoadPagePanel} from "../../../../components";
import CountertopUniversityCard from "./CountertopUniversityCard";
import {useEffect, useState} from "react";
import CountertopUniversityLeaf from "./CountertopUniversityLeaf";
import {useTitle} from "../../../../contexts/title";
import {BreadcrumbLink} from "../../../../types";
import CountertopProfileSelectionPopup from "./CountertopProfileSelectionPopup";
import {useCountertopUniversity} from "../contexts/countertopUniversityContext";
import CountertopUniversityCertificationPopup from "./CountertopUniversityCertificationPopup";
import {CountertopUniversityType} from "../types/CountertopUniversityType";
import {showErrorMessage} from "../../../../utils/messages";
import banner from '../img/sage_solutions_banner.png';
import {useAuth} from "../../../../contexts/auth";
import {CompanyTypes} from "../../../../types/enums";
import CountertopUniversityCourseCard from "./CountertopUniversityCourseCard";
import {useGetCountertopUniversity} from "../api/get-countertop-university";
import {Button, SelectBox} from "devextreme-react";
import {useGetRetailers} from "../../../../api/retailers";

export default function CountertopUniversityForm() {
    const context = useCountertopUniversity();

    const parameters = useParams()['*'];
    const {user} = useAuth();
    const {setDynamicLinks, setTitle} = useTitle();

    const [parentCompanyID, setParentCompanyID] = useState<number | null>(null);

    const {data: parentCompanies} = useGetRetailers();

    const isDealer = user?.companyTypeId === CompanyTypes.DEALER;

    const {data: recordsData, isFetching: isLoading, refetch} = useGetCountertopUniversity(
        parentCompanyID,
        context.profileID,
        isDealer ? !!context.profileID : true
    );

    useEffect(() => {
        if (!recordsData) return;

        let links = [] as BreadcrumbLink[];
        if (parameters !== '') {
            links.push({
                text: 'Sage Solutions Institute',
                path: '/countertop-university'
            });
            let segments = parameters?.split('/') || [];
            let root = '/';
            for (let i = 0; i < segments.length - 1; i++) {
                let item = recordsData.universityDetails?.find(x => x.countertopUniversityID === Number(segments[i]));
                if (item !== undefined) {
                    links.push({
                        text: item.title,
                        path: '/countertop-university' + root + item.countertopUniversityID
                    });
                    root = root + item.countertopUniversityID + '/';
                }
            }

            let item = recordsData.universityDetails?.find(x => x.countertopUniversityID === Number(segments[segments.length - 1]));

            context.setCountertopUniversityId(item?.countertopUniversityID!);

            setTitle(item?.title ?? '');
            context.setCourseTitle(item?.title ?? null);
        } else {
            setTitle('Sage Solutions Institute');
            context.setCourseTitle(null);
        }
        setDynamicLinks(links);

        return () => {
            setDynamicLinks([]);
        };
    }, [parameters, recordsData, context]);

    // For non-dealers: reset parent company filter when profile changes
    useEffect(() => {
        if (!isDealer) {
            setParentCompanyID(null);
        }
    }, [user?.companyTypeId, context.profileID]);

    useEffect(() => {
        context.setOnDataRefresh(() => () => refetch());

        return () => {
            context.setOnDataRefresh(null);
        };
    }, [refetch, context]);

    useEffect(() => {
        if (user?.companyTypeId === CompanyTypes.DEALER && !context.profileID) {
            context.setProfilePopupVisible(true);
        }
    }, [context.profileID, user?.companyTypeId, context]);

    function getLatestId() {
        if (parameters === '') {
            return '';
        } else {
            let idx = parameters?.lastIndexOf('/') ?? 0;
            if (idx !== 0) {
                idx++;
            }
            return parameters?.substring(idx) || '';
        }
    }

    let id = getLatestId();

    let childCount = 0;
    let currentItem = recordsData?.universityDetails?.find(x => x.countertopUniversityID === Number(id));

    if (currentItem && recordsData) {

        var parent = recordsData.universityDetails?.find(
            (f) => f.countertopUniversityID === currentItem!.parentCountertopUniversityID);

        var isParentCertification = parent?.certificationDone;
        if (isParentCertification && currentItem!.universityType === CountertopUniversityType.Certification) {
            currentItem.certUrl = parent!.certUrl;
            currentItem.certificationDone = parent!.certificationDone;
        }

        let hasOtherVideos = recordsData?.universityDetails?.filter(f =>
            f.parentCountertopUniversityID === currentItem!.parentCountertopUniversityID
            && f.countertopUniversityID > Number(id)
            && f.videoUrl)
            .sort((a, b) => a.countertopUniversityID - b.countertopUniversityID)

        if (hasOtherVideos.length > 0) {
            let nextVideo = hasOtherVideos[0];
            currentItem.nextVideoUrl = nextVideo
                ? `/countertop-university/${currentItem.parentCountertopUniversityID}/${nextVideo.countertopUniversityID}`
                : "";
        }
    }

    const handleGenerateCertificate = (parentCountertopUniversityID: number) => {
        if (!context.certificationPopupVisible) {
            const certDetail = recordsData?.universityDetails?.filter(x =>
                x.universityType === CountertopUniversityType.Certification
                && !x.certificationDone
                && x.parentCountertopUniversityID === parentCountertopUniversityID);

            var cert = certDetail && certDetail.length > 0 ? certDetail[0] : null;

            if (cert === null) {
                showErrorMessage("No certification available to generate. Contact administrator for help.");
                return;
            }

            context.setCountertopUniversityId(cert?.parentCountertopUniversityID ?? null);
            context.setCertificationPopupVisible(true);
        }
    }

    const handleSearchByParentCompany = () => refetch();
    
    return (
        <div className={'content-block'}>
            {context.certificationPopupVisible && <CountertopUniversityCertificationPopup/>}

            <CountertopProfileSelectionPopup/>

            {!context.courseTitle && <img src={banner} className='countertop-university-banner'/>}

            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <p className="countertop-university-form-section-title">{context.courseTitle || 'My Courses'}</p>
                {
                    !context.courseTitle && user?.companyTypeId !== CompanyTypes.DEALER &&
                    <div style={{display: 'flex', gap: '10px', alignItems: 'center'}}>
                        <SelectBox
                            valueExpr='companyID'
                            displayExpr='companyName'
                            placeholder="Select Parent Company"
                            dataSource={parentCompanies}
                            value={parentCompanyID}
                            onValueChanged={(e) => setParentCompanyID(e.value)}
                            searchEnabled={true}
                            inputAttr={{"aria-label": "Search By"}}
                            dropDownOptions={{wrapperAttr: {class: 'header-search-dropdown'}}}
                        />
                        <Button
                            text=""
                            icon="dx-icon dx-icon-search"
                            className='search-button'
                            type="default"
                            style={{
                                width: '28px',
                                height: '28px',
                                borderRadius: '4px',
                                padding: '0',
                                fontSize: '16px',
                                backgroundColor: '#404143'
                            }}
                            onClick={handleSearchByParentCompany}>
                        </Button>
                    </div>
                }
            </div>

            {context.profileFullName && user?.companyTypeId === CompanyTypes.DEALER && (
                <span className="countertop-university-welcome-container">
                    Welcome {context.profileFullName}
                    <a
                        className="profile-link-text"
                        onClick={() => {
                            context.setProfilePopupVisible(true);
                        }}
                    >
                        Not you? Change Profile
                    </a>
                </span>
            )}

            <div className="countertop-university-cards">
                {recordsData?.universityDetails?.map((x) => {
                    if (id === '') {
                        if (x.parentCountertopUniversityID === null) {
                            x.allowTakeQuiz = true;
                            childCount++;

                            return x.universityType === CountertopUniversityType.Course || x.universityType === CountertopUniversityType.Academy ?
                                <CountertopUniversityCourseCard key={x.countertopUniversityID} item={x}/> :
                                <CountertopUniversityCard key={x.countertopUniversityID} item={x}/>;
                        }
                    } else {
                        if (x.parentCountertopUniversityID === Number(id)) {

                            var parent = recordsData.universityDetails?.find(
                                (f) => f.countertopUniversityID === x.parentCountertopUniversityID);

                            var isParentCertification = parent?.certificationDone;
                            if (isParentCertification && x.universityType === CountertopUniversityType.Certification) {
                                x.certUrl = parent!.certUrl;
                                x.certificationDone = parent!.certificationDone;
                            }

                            x.allowTakeQuiz = true;
                            if (x.surveyLink) {
                                let siblingsWithVideo =
                                    recordsData.universityDetails.filter(
                                        (f) =>
                                            f.parentCountertopUniversityID === x.parentCountertopUniversityID &&
                                            f.videoUrl
                                    ) ?? [];
                                x.allowTakeQuiz = siblingsWithVideo.filter((f) => !f.videoDone).length < 1;
                            }

                            if (x.universityType === CountertopUniversityType.Certification) {
                                const siblingSurvey = recordsData.universityDetails.find(
                                    (f) =>
                                        f.parentCountertopUniversityID === x.parentCountertopUniversityID
                                        && f.universityType === CountertopUniversityType.Survey
                                        && !!f.lastSurveyAttempt
                                );
                                x.lastSurveyAttempt = siblingSurvey ? siblingSurvey.lastSurveyAttempt : undefined;
                                x.surveyDone = siblingSurvey ? siblingSurvey.surveyDone : false;
                            }
                            childCount++;

                            return <CountertopUniversityCard
                                key={x.countertopUniversityID}
                                item={x}
                                onGenerateCertificateClick={handleGenerateCertificate}
                                allItems={recordsData?.universityDetails}
                            />;
                        }
                    }
                })}
            </div>
            {currentItem && childCount === 0 &&
                <CountertopUniversityLeaf
                    item={currentItem}
                    onVideoComplete={() => refetch()}
                    onQuizComplete={() => refetch()}
                />
            }
            <LoadPagePanel visible={isLoading}/>
        </div>
    )
}